import { CommonModule } from '@angular/common';
import { Component, HostListener, OnDestroy, OnInit, Signal, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import {
  BackNavigable,
  BackNavigationService,
  CommandStore,
  MengenEingabeComponent,
  Scan,
  ScannerStore,
  showRequestErrors,
  SOFTLINE_FEATURE_COMMANDS,
  SOFTLINE_FEATURE_SCANNER } from '@softline/application';
import { Store } from '@softline/core';
import { ModalStore, SOFTLINE_FEATURE_MODAL} from '@softline/ui-core';
import { map, of, skip, Subscription, switchMap } from 'rxjs';
import { ArtikelHeaderComponent } from '../../../components/artikel-header/artikel-header.component';
import { PaletteAendernDialogComponent } from '../../../dialogs/palette-aendern/palette-aendern-dialog.component';
import {
  SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG,
  SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG_PALETTE } from '../../../kommissionierung.shared';
import { RemoteConfigService } from '../../../services/remote-config.service';
import { LlrBestellungStore } from '../../../store/bestellung.store';
import { LlrKommissionierungStore } from '../../../store/kommissionierung.store';
import { LlrPalleteStore } from '../../../store/palette.store';
import {
  Kommissionierung,
  KommissionierungBewegung } from '../../../types/kommissionierung';
import { Palette } from '../../../types/palette';

import { RepeatDirective } from '@softline/ui-core';
@Component({
  selector: 'soft-artikeldaten-erfassung',
  imports: [
    CommonModule,
    MengenEingabeComponent,
    ArtikelHeaderComponent,
    RouterOutlet,
    RepeatDirective],
  templateUrl: './artikeldaten-erfassung.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./artikeldaten-erfassung.component.css'] })
export class ArtikeldatenErfassungComponent implements OnInit, OnDestroy, BackNavigable {
  public kommissionierung?: Kommissionierung;
  public vorhandenePalettenAuswaehlbar = false;

  private paletteId = '';
  private kommissionierungSubscription?: Subscription;
  private scanSubscription?: Subscription;

  private readonly palettenId$ = this.activatedRoute.queryParamMap.pipe(
    map((queryParamMap) => queryParamMap.get('paletteId') ?? null)
  );

  readonly gewichtEditable = this.remoteConfig.config.gewichtEditable;
  readonly paletteEinzelEingabe = this.remoteConfig.config.paletteEinzelEingabe;

  readonly palettenVorhanden = toSignal(
    this.activatedRoute.queryParamMap.pipe(
      map((queryParamMap) => queryParamMap.get('palettenVorhanden') ?? '')
    )
  );

  readonly kommissionierung$ = this.store.observe(
    SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG,
    LlrKommissionierungStore.getters.data
  );

  readonly bewegung = toSignal(
    this.activatedRoute.paramMap.pipe(
      map((paramMap) => paramMap.get('id') ?? ''),
      switchMap((id) =>
        this.kommissionierung$.pipe(
          map((komm) => {
            const index = komm?.bewebskomms.findIndex((o) => o.id + '' === id);

            if (index < 0) return null;

            return komm?.bewebskomms[index];
          })
        )
      )
    )
  );

  readonly isNeuePalette = toSignal(
    this.palettenId$.pipe(
      map((palettenId) => palettenId === null || palettenId?.trim() === '')
    )
  );

  readonly palette: Signal<Palette | null | undefined> = toSignal(
    this.palettenId$.pipe(
      switchMap((palletenId) => {
        if (palletenId !== null && palletenId?.trim() !== '') {
          this.paletteId = palletenId;
          return this.store.observe(
            SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG_PALETTE,
            LlrPalleteStore.getters.entity,
            palletenId
          );
        } else {
          return of(null);
        }
      })
    ),
    { initialValue: null }
  );

  readonly loading$ = this.store.observe(
    SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG_PALETTE,
    LlrBestellungStore.getters.loading
  );

  @HostListener('window:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    event.stopPropagation();
    event.stopImmediatePropagation();
  }

  constructor(
    private store: Store,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private remoteConfig: RemoteConfigService,
    private backNavigationService: BackNavigationService
  ) {}

  async ngOnInit(): Promise<void> {
    this.backNavigationService.set(this);

    this.store.commit(SOFTLINE_FEATURE_COMMANDS, CommandStore.mutations.addSet, {
      name: ArtikeldatenErfassungComponent,
      commands: [
        {
          name: '#LLR_KOMMISSIONIERUNG.AKTIONEN.LABEL_PALETTE_AENDERN',
          isVisible: of(!this.isNeuePalette()),
          class: 'menu action-menu action-menu-top',
          icon: 'fa-regular fa-pen-to-square',
          execute: async () =>
            this.openPaletteAendernDialog(this.palette(), this.bewegung()) },
        {
          name: '#LLR_KOMMISSIONIERUNG.AKTIONEN.LABEL_ARTIKELDATEN_SCANNEN',
          class: 'menu action-menu action-menu-top',
          icon: 'fa-regular fa-barcode-scan',
          execute: async () => this.scanKommissionierungDaten() }] });

    this.kommissionierungSubscription = this.kommissionierung$.subscribe((komm) => {
      this.kommissionierung = komm;
    });

    try {
      const bewe = this.bewegung();

      if (bewe) {
        if (!this.palette() && this.paletteId) {
          await this.store.dispatch(
            SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG_PALETTE,
            LlrPalleteStore.actions.loadPalatte,
            {
              idpalette: this.paletteId ?? '' }
          );
        }
      }
    } catch (e) {
      showRequestErrors(this.store, e);
    }
    this.vorhandenePalettenAuswaehlbar =
      this.remoteConfig.config.vorhandenePalettenAuswaehlbar;

    this.scanSubscription = this.store
      .observe(SOFTLINE_FEATURE_SCANNER, ScannerStore.getters.latest)
      .pipe(skip(1))
      .subscribe(async (scan) => {
        await this.scanKommissionierungDaten(scan);
      });
  }

  ngOnDestroy(): void {
    this.store.commit(
      SOFTLINE_FEATURE_COMMANDS,
      CommandStore.mutations.removeSet,
      ArtikeldatenErfassungComponent
    );

    if (this.kommissionierungSubscription && !this.kommissionierungSubscription.closed)
      this.kommissionierungSubscription.unsubscribe();

    if (this.scanSubscription && !this.scanSubscription.closed)
      this.scanSubscription.unsubscribe();

    this.backNavigationService.set(undefined);
    this.kommissionierungSubscription = undefined;
    this.scanSubscription = undefined;
  }

  async navigateBack(): Promise<void> {
    if (this.palettenVorhanden() === 'false' || !this.vorhandenePalettenAuswaehlbar) {
      await this.router.navigate([`/llr-bskomm/artikelauswahl`]);
      return;
    } else {
      if (this.paletteId.length > 0) {
        await this.router.navigate([
          `/llr-bskomm/bewegung/${this.bewegung()?.id}/palettenauswahl`]);
        return;
      }
      await this.router.navigate([
        `/llr-bskomm/bewegung/${this.bewegung()?.id}/palettenart-auswahl`]);
    }
  }

  private async openPaletteAendernDialog(
    palette?: Palette | null,
    bewegung?: KommissionierungBewegung | null
  ) {
    try {
      if (palette) {
        const paletteParam = await this.store.dispatch(
          SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG_PALETTE,
          LlrPalleteStore.actions.loadPalatte,
          {
            idpalette: palette.id + '' }
        );

        const result = await this.store.dispatch(
          SOFTLINE_FEATURE_MODAL,
          ModalStore.actions.open<Palette | undefined, object>(),
          {
            id: 'SOFTLINE_DIALOG_KOMMISSIONIERUNG_PALETTE_AENDERN',
            component: PaletteAendernDialogComponent,
            data: {
              palette: paletteParam,
              bewegung: bewegung,
              gewichtEditable: this.gewichtEditable },
            dismiss: true }
        );

        if (!result || result === 'DISMISSED') return;

        await this.store.dispatch(
          SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG_PALETTE,
          LlrPalleteStore.actions.palettenMengeUebernehmen,
          {
            idpalette: paletteParam?.id + '',
            payload: result }
        );
      }
    } catch (e) {
      showRequestErrors(this.store, e);
    }
  }

  private async scanKommissionierungDaten(scan?: Scan) {
    try {
      let scanParam;

      if (!scan) {
        scanParam = await this.store.dispatch(
          SOFTLINE_FEATURE_SCANNER,
          ScannerStore.actions.scan,
          {
            labelType: ['ean13', 'ean128', 'code39', 'code128', 'ean8'] }
        );
      } else {
        scanParam = scan;
      }

      if (!scanParam) return;

      const bewe = this.bewegung();

      if (!bewe) return;

      const result = await this.store.dispatch(
        SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG,
        LlrKommissionierungStore.actions.scanKommissionierungDaten,
        {
          idkopfbskomm: bewe.idkopfbskomm + '',
          idbewebskomm: bewe.id + '',
          type: scanParam.labelType ?? '',
          code: scanParam.data ?? '' }
      );

      if (!result) return;
    } catch (e) {
      showRequestErrors(this.store, e);
    }
  }
}
